/**
 * Salvage layout maths (M2-10, spec §6.2): where a layout slot of `salvage.json` sits on a ship model, and the
 * wreck-frame → world transform every salvage system shares.
 *
 * A slot is either a named socket of the ship model or a point across the model's bounds box (−1..1 per axis),
 * so a swapped (Meshy) model keeps its layout as long as it keeps the socket names and a sensible bounds box.
 * Positions come out in the model's own frame, which is the wreck frame of a ship wreck (plan §2.7 `WreckPool`).
 */
import type { Vec3 } from '../core/math'
import type { ModelEntry } from '../data/assetSchema'
import type { SlotAnchor } from '../data/m2Schemas'

/** The parts of a model entry a layout reads. */
export type SlotModel = Pick<ModelEntry, 'sockets' | 'bounds'>

/**
 * Writes the model-frame position of `slot` into `out`. A `bounds` slot maps `[u, v, w]` linearly across the
 * bounds box (−1 = min, 0 = the box centre, +1 = max). Returns false (and writes the box centre) when a socket slot
 * names a socket the model does not have, so a swapped model missing a socket degrades instead of throwing.
 */
export function slotPosition(slot: SlotAnchor, model: SlotModel, out: Vec3): boolean {
  const { min, max } = model.bounds
  if ('bounds' in slot) {
    const [u, v, w] = slot.bounds
    out.x = (min[0] + max[0]) * 0.5 + u * (max[0] - min[0]) * 0.5
    out.y = (min[1] + max[1]) * 0.5 + v * (max[1] - min[1]) * 0.5
    out.z = (min[2] + max[2]) * 0.5 + w * (max[2] - min[2]) * 0.5
    return true
  }
  const socket = model.sockets.find((s) => s.name === slot.socket)
  if (socket) {
    out.x = socket.position[0]
    out.y = socket.position[1]
    out.z = socket.position[2]
    return true
  }
  boxCentre(model, out)
  return false
}

/** The centre of the model's bounds box (the reference point for "outward" on a ship wreck). */
export function boxCentre(model: SlotModel, out: Vec3): Vec3 {
  const { min, max } = model.bounds
  out.x = (min[0] + max[0]) * 0.5
  out.y = (min[1] + max[1]) * 0.5
  out.z = (min[2] + max[2]) * 0.5
  return out
}

/**
 * World position of a wreck-frame point: `origin + q · (lx, ly, lz)`. The origin is float64 world metres and the
 * result stays float64 (D-011); allocation-free (D-014), `out` is caller-owned.
 */
export function wreckToWorld(
  ox: number,
  oy: number,
  oz: number,
  qx: number,
  qy: number,
  qz: number,
  qw: number,
  lx: number,
  ly: number,
  lz: number,
  out: Vec3,
): Vec3 {
  const tx = 2 * (qy * lz - qz * ly)
  const ty = 2 * (qz * lx - qx * lz)
  const tz = 2 * (qx * ly - qy * lx)
  out.x = ox + lx + qw * tx + (qy * tz - qz * ty)
  out.y = oy + ly + qw * ty + (qz * tx - qx * tz)
  out.z = oz + lz + qw * tz + (qx * ty - qy * tx)
  return out
}
